import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Invoice, Tenant } from '../../api/types.js';
import { formatAmount, formatInvoiceDate } from '../invoices/format.js';
import { resolveDueDate } from '../invoices/dueDate.js';
import { StateBadge } from '../invoices/StateBadge.js';
import { copyToClipboard } from '../../lib/clipboard.js';
import { EmptyState } from '../../components/EmptyState.js';

interface Props {
  invoices: Invoice[];
  tenants: Tenant[];
}

function tenantShort(tenants: Tenant[], tenantId: string): string {
  const t = tenants.find((x) => x.tenantId === tenantId);
  if (!t) return tenantId.slice(0, 8);
  const n = t.legalName;
  if (/altyor industrie/i.test(n)) return 'AIF';
  if (/altyor holding/i.test(n)) return 'Holding';
  return n;
}

function reminderText(inv: Invoice, dueDisplay: string, isEstimated: boolean): string {
  const amount = formatAmount(inv.amountTotal ?? null, inv.currency ?? null);
  const supplier = inv.sender?.name ?? '—';
  const num = inv.invoiceNumber ?? inv.primaryKey;
  const echeance = isEstimated ? `échéance estimée ${dueDisplay}` : `échéance ${dueDisplay}`;
  return `Bonjour, merci de traiter la facture ${num} de ${supplier} (${amount}) — ${echeance}.`;
}

export function AssignedToMeWidget({ invoices, tenants }: Props) {
  const [copied, setCopied] = useState<string | null>(null);

  async function copyReminder(inv: Invoice, dueDisplay: string, isEstimated: boolean) {
    const text = reminderText(inv, dueDisplay, isEstimated);
    try {
      await copyToClipboard(text);
      setCopied(inv.primaryKey);
      window.setTimeout(() => setCopied((c) => (c === inv.primaryKey ? null : c)), 1500);
    } catch {
      setCopied(null);
    }
  }

  return (
    <section className="dashboard-widget" data-testid="widget-assigned">
      <header className="dashboard-widget__head">
        <h2 className="display dashboard-widget__title">Mes factures assignées</h2>
        <span className="dashboard-widget__count" data-testid="widget-assigned-count">
          {invoices.length}
        </span>
      </header>
      {invoices.length === 0 ? (
        <EmptyState
          testId="widget-assigned-empty"
          variant="inline"
          icon="📭"
          title="Aucune facture assignée"
          description="Vous n'avez pas de facture à traiter pour le moment."
        />
      ) : (
        <ul className="dashboard-rows" data-testid="widget-assigned-list">
          {invoices.map((inv) => {
            const due = resolveDueDate(inv.dueDate, inv.invoiceDate ?? null);
            const dueDisplay = due.iso ? formatInvoiceDate(due.iso) : '—';
            return (
              <li
                key={inv.primaryKey}
                className="dashboard-row"
                data-testid={`assigned-row-${inv.primaryKey}`}
              >
                <span className="pill pill--neutral dashboard-row__tenant">
                  {tenantShort(tenants, inv.tenantId)}
                </span>
                <Link
                  to={`/t/${inv.tenantId}/invoices/${encodeURIComponent(inv.primaryKey)}`}
                  className="dashboard-row__main"
                >
                  <span className="dashboard-row__supplier">{inv.sender?.name ?? '—'}</span>
                  <span className="muted">{inv.invoiceNumber ?? inv.primaryKey}</span>
                </Link>
                <span className="dashboard-row__amount">
                  {formatAmount(inv.amountTotal ?? null, inv.currency ?? null)}
                </span>
                <StateBadge state={inv.state} dispute={inv.dispute ?? null} />
                <span className="dashboard-row__due">
                  {dueDisplay}
                  {due.isEstimated ? (
                    <span
                      className="pill pill--neutral dashboard-row__estimated"
                      data-testid={`assigned-row-${inv.primaryKey}-estimated`}
                    >
                      estimée
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="btn-ghost dashboard-row__copy"
                  onClick={() => void copyReminder(inv, dueDisplay, due.isEstimated)}
                  data-testid={`assigned-row-${inv.primaryKey}-copy`}
                >
                  {copied === inv.primaryKey ? 'Copié !' : 'Copier rappel Teams'}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export { reminderText };
